import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Row, Col, Form, Button, Spinner } from "react-bootstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import TelaComunicado from "./TelaComunicado";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import useExcluirAtividade from "./hooks/useExcluirAtividade";
import useAssinarAtividade from "./hooks/useAssinarAtividade";
import { RetornaDataToNumero } from "../../../components/Formatters/DateTime";
import useSetGuard from "../../../hooks/useSetGuard";
import ReplaceChar from "../../../components/Formatters/ReplaceChar";
import useGerarComunicado from "./hooks/useGerarComunicado";
import FormRelCircunstanciado from "../AutoInfracaoEspecial/components/FormRelCircunstanciado";
import ConsultaAnexos from "../Anexos/ConsultaAnexos";
import SelectFileInput from "../../../components/UI/SelectFileInput/SelectFileInput";

const validationSchema = Yup.object().shape({
  textoImpedimento: Yup.string()
    .default("")
    .required("Informe o Texto da Atividade")
    .min(1, "Texto muito curto")
    .test("verifica-tag", " => Necessário conter tag [MOTIVO] no texto da atividade", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "INAUDALIMP" && value.indexOf("[MOTIVO]") === -1) return false;
      return true;
    }),
  anexos: Yup.array()
    .default([])
    .test(
      "integrity-anexos",
      "=> Verifique os arquivos em que constam erros, remova-os e tente a seleção novamente. NECESSÁRIO FAZER UPLOAD! ",
      (value) => {
        // Verificar se uploads estão OK e se não há erros
        if (value?.length > 0) {
          for (let i = 0; i < (value || []).length; i += 1)
            if (value[i].error || value[i].uploading || value[i].file) return false;
        }
        return true;
      },
    ),
  sequencialAtividade: Yup.string().default(),
  impedimentos: Yup.array()
    .default([])
    .test("qtd-impedimentos", "Selecione pelo menos 1 motivo de impedimento", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "INAUDALIMP" && (value || []).length === 0) return false;
      return true;
    }),
  siglaAtividade: Yup.string().default(""),
  listaExecutoresOs: Yup.array()
    .default([])
    .test("qtd-executor", "Selecione pelo menos 1 executor", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "DISUBAUDOS" &&
        value.length === 0 &&
        atividade.parent.totalExecutores === 0
      )
        return false;
      return true;
    }),
  totalExecutores: Yup.number().default(0),
  cpfRepresentante: Yup.string()
    .default("")
    .test("cpf-valido", "Digite CPF válido", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "I4" ||
        atividade.parent.siglaAtividade === "I10" ||
        atividade.parent.siglaAtividade === "IF4" ||
        atividade.parent.siglaAtividade === "I11" ||
        atividade.parent.siglaAtividade === "IF10" ||
        atividade.parent.siglaAtividade === "IF11" ||
        atividade.parent.siglaAtividade === "M1" ||
        atividade.parent.siglaAtividade === "P6" ||
        atividade.parent.siglaAtividade === "D1" ||
        atividade.parent.siglaAtividade === "AUIMPRREDO" ||
        atividade.parent.siglaAtividade === "AUINCIPEAI" ||
        atividade.parent.siglaAtividade === "AUPRTERECO"
      ) {
        if ((value || []).length !== 14) return false;
      }
      return true;
    }),
  nomeRepresentante: Yup.string()
    .default("")
    .test("nome-valido", "Digite nome do representante", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "I4" ||
        atividade.parent.siglaAtividade === "I10" ||
        atividade.parent.siglaAtividade === "IF4" ||
        atividade.parent.siglaAtividade === "I11" ||
        atividade.parent.siglaAtividade === "IF10" ||
        atividade.parent.siglaAtividade === "IF11" ||
        atividade.parent.siglaAtividade === "M1" ||
        atividade.parent.siglaAtividade === "P6" ||
        atividade.parent.siglaAtividade === "D1" ||
        atividade.parent.siglaAtividade === "AUIMPRREDO" ||
        atividade.parent.siglaAtividade === "AUINCIPEAI" ||
        atividade.parent.siglaAtividade === "AUPRTERECO"
      ) {
        if ((value || []).length === 0) return false;
      }

      return true;
    }),
  cargoRepresentante: Yup.string()
    .default("")
    .test("cargo-valido", "Digite cargo do representante", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "I4" ||
        atividade.parent.siglaAtividade === "I10" ||
        atividade.parent.siglaAtividade === "IF4" ||
        atividade.parent.siglaAtividade === "I11" ||
        atividade.parent.siglaAtividade === "IF10" ||
        atividade.parent.siglaAtividade === "IF11" ||
        atividade.parent.siglaAtividade === "M1" ||
        atividade.parent.siglaAtividade === "P6" ||
        atividade.parent.siglaAtividade === "D1" ||
        atividade.parent.siglaAtividade === "AUINCIPEAI" ||
        atividade.parent.siglaAtividade === "AUPRTERECO"
      ) {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  nuPaginaEdital: Yup.string()
    .default("")
    .test("numero-valido", "Digite Nº do Diário Oficial", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "I8" ||
        atividade.parent.siglaAtividade === "IF8" ||
        atividade.parent.siglaAtividade === "IF8" ||
        atividade.parent.siglaAtividade === "P10"
      ) {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  dataComunicado: Yup.date()
    .test("data-valida-atual", "Data da Ciência não pode ser maior que a data de hoje.", (value, atividade) => {
      if (
        (RetornaDataToNumero(value) > RetornaDataToNumero(new Date()) &&
          (atividade.parent.siglaAtividade === "I4" ||
            atividade.parent.siglaAtividade === "I8" ||
            atividade.parent.siglaAtividade === "IF8" ||
            atividade.parent.siglaAtividade === "I10" ||
            atividade.parent.siglaAtividade === "IF4" ||
            atividade.parent.siglaAtividade === "IF8" ||
            atividade.parent.siglaAtividade === "IF10" ||
            atividade.parent.siglaAtividade === "I13" ||
            atividade.parent.siglaAtividade === "IF13" ||
            atividade.parent.siglaAtividade === "M1" ||
            atividade.parent.siglaAtividade === "M4" ||
            atividade.parent.siglaAtividade === "D1" ||
            atividade.parent.siglaAtividade === "D4" ||
            atividade.parent.siglaAtividade === "P6" ||
            atividade.parent.siglaAtividade === "D4" ||
            atividade.parent.siglaAtividade === "P10" ||
            atividade.parent.siglaAtividade === "P15" ||
            atividade.parent.siglaAtividade === "D1")) ||
        atividade.parent.siglaAtividade === "AUINCIPEAI"
      ) {
        if (atividade.parent.siglaAtividade === "IF4") {
          return false;
        }
      }
      return true;
    })
    .test(
      "data-valida-anterior",
      "Data da Ciência não pode ser menor que a data do termo de intimação.",
      (value, atividade) => {
        if (
          (RetornaDataToNumero(value) < RetornaDataToNumero(atividade.parent.dataUltimaAtividadeExecutada) &&
            (atividade.parent.siglaAtividade === "I4" ||
              atividade.parent.siglaAtividade === "I8" ||
              atividade.parent.siglaAtividade === "IF8" ||
              atividade.parent.siglaAtividade === "I10" ||
              atividade.parent.siglaAtividade === "IF4" ||
              atividade.parent.siglaAtividade === "IF8" ||
              atividade.parent.siglaAtividade === "IF10" ||
              atividade.parent.siglaAtividade === "I13" ||
              atividade.parent.siglaAtividade === "IF13" ||
              atividade.parent.siglaAtividade === "M1" ||
              atividade.parent.siglaAtividade === "M4" ||
              atividade.parent.siglaAtividade === "D1" ||
              atividade.parent.siglaAtividade === "D4" ||
              atividade.parent.siglaAtividade === "P6" ||
              atividade.parent.siglaAtividade === "D4" ||
              atividade.parent.siglaAtividade === "P10" ||
              atividade.parent.siglaAtividade === "P15" ||
              atividade.parent.siglaAtividade === "D1")) ||
          atividade.parent.siglaAtividade === "AUINCIPEAI"
        ) {
          if (atividade.parent.siglaAtividade === "IF4") {
            return false;
          }
        }
        return true;
      },
    ),
  codigoRastreio: Yup.string()
    .default("")
    .test("codigo-valido", "Digite o código de rastreio", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "I9" ||
        atividade.parent.siglaAtividade === "IF9" ||
        atividade.parent.siglaAtividade === "P11"
      ) {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  livrosFiscais: Yup.array()
    .default([])
    .test("qtd-livrosFiscais", "Selecione pelo menos um ano de um livro fiscal", (value, atividade) => {
      if (
        ((atividade.parent.siglaAtividade === "IF1" ||
          atividade.parent.siglaAtividade === "IF2" ||
          atividade.parent.siglaAtividade === "IF3") &&
          (value || []).length === 0) ||
        value === null
      )
        return false;
      return true;
    }),
  txDescricao: Yup.string()
    .default("")
    .test("texto-valido", "Digite texto de descrição", (value, atividade) => {
      if (
        atividade.parent.siglaAtividade === "DEDIJUSORE" ||
        atividade.parent.siglaAtividade === "R1" ||
        atividade.parent.siglaAtividade === "AUGEIMTEOC" ||
        atividade.parent.siglaAtividade === "M5" ||
        atividade.parent.siglaAtividade === "M0"
      ) {
        if ((value || []).length === 0 || value === undefined || value === null || value === "<p><br></p>")
          return false;
      }
      return true;
    }),
  fgAnexoBo: Yup.boolean().default(false),
  fgAnexoJornal: Yup.boolean().default(false),
  txParametrizacao: Yup.array()
    .default([])
    .test("valida-itens", "Selecione pelo menos 1 motivo do arbitramento", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "AULATERARB" && (value || []).length === 0) return false;
      return true;
    }),
  txJustificativa: Yup.string()
    .default("")
    .test("valida-just", "Digite um fato justificador", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "AULATERARB" || atividade.parent.siglaAtividade === "AUINJUFOCO") {
        if ((value || []).length === 0 || value === undefined || value === null || value === "<p><br></p>")
          return false;
      }
      return true;
    }),
  fgMonitoramento: Yup.boolean().default(false),

  sequencialTipoAcaoFiscalNovo: Yup.string()
    .default("")
    .test("valida-novo-tipo", "Selecione uma tipo de ação fiscal.", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "M6") {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),

  procedimentos: Yup.array().default([]),

  vlIcmsDevido: Yup.string()
    .default("")
    .test("texto-valido", "Valor obrigatório", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "M5") {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  vlIcmsRecolhido: Yup.string()
    .default("")
    .test("texto-valido", "Valor obrigatório", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "M5") {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),

  vlIcmsParcelado: Yup.string()
    .default("")
    .test("texto-valido", "Valor obrigatório", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "M5") {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  vlIcmsEstornado: Yup.string()
    .default("")
    .test("texto-valido", "Valor obrigatório", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "M5") {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  cdProcesso: Yup.string()
    .default("")
    .test("codigo-valido", "Número do Processo obrigatório", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "M5" && parseInt(atividade.parent.vlIcmsParcelado, 10) > 0) {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  enderecoRepresentante: Yup.string()
    .default("")
    .test("endereco-valido", "Digite o endereço do representante", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "AUPRTERECO") {
        if ((value || []).length === 0 || value === undefined || value === null) return false;
      }
      return true;
    }),
  txPreliminares: Yup.string()
    .default("")
    .test("texto-valido", "Texto obrigatório", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "AI1") {
        if ((value || []).length === 0 || value === undefined || value === null || value === "<p><br></p>")
          return false;
      }
      return true;
    }),
  txProcedimentos: Yup.string()
    .default("")
    .test("texto-valido", "Texto obrigatório", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "AI1") {
        if ((value || []).length === 0 || value === undefined || value === null || value === "<p><br></p>")
          return false;
      }
      return true;
    }),
  txConstatacoes: Yup.string()
    .default("")
    .test("texto-valido", "Texto obrigatório", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "AI1") {
        if ((value || []).length === 0 || value === undefined || value === null || value === "<p><br></p>")
          return false;
      }
      return true;
    }),
  txOcorrencias: Yup.string()
    .default("")
    .test("texto-valido", "Texto obrigatório", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "AI1") {
        if ((value || []).length === 0 || value === undefined || value === null || value === "<p><br></p>")
          return false;
      }
      return true;
    }),
  txResultado: Yup.string()
    .default("")
    .test("texto-valido", "Texto obrigatório", (value, atividade) => {
      if (atividade.parent.siglaAtividade === "AI1") {
        if ((value || []).length === 0 || value === undefined || value === null || value === "<p><br></p>")
          return false;
      }
      return true;
    }),
  cdPgeProcesso: Yup.string().test("cod_valido", "O Número do Processo é obrigatório", function (value) {
    const { siglaAtividade } = this.parent;

    if (siglaAtividade === "PATANAPROC") {
      if (!value) return false;

      // valida tamanho exato
      if (value.length !== 23) return false;

      // valida formato
      const regex = /^\d{8}\.\d{6}\/\d{4}-\d{2}$/;
      return regex.test(value);
    }

    return true;
  }),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  if (
    key === "anexos" ||
    key === "impedimentos" ||
    key === "listaExecutoresOs" ||
    key === "livrosFiscais" ||
    key === "txParametrizacao" ||
    key === "procedimentos"
  ) {
    initialValues[key] = [];
  } else if (key === "fgAnexoBo" || key === "fgAnexoJornal" || key === "fgMonitoramento") {
    initialValues[key] = false;
  } else if (key === "vlIcmsDevido" || key === "vlIcmsRecolhido") {
    initialValues[key] = 0;
  } else {
    initialValues[key] = "";
  }
});

const InputsProximaAtividadeBase = ({ dadosOs, atividade }) => {
  const { request: salvarRequest, data: salvarData, errors: salvarErrors, isLoading: salvarIsLoading } = useApiClient();
  const [desabilitaAssinar, setDesabilitaAssinar] = useState(false);
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const parametros = {
        sequencialComunicado: dadosOs.sequencialComunicado,
        sequencialAcaoFiscal: dadosOs.acaoFiscal,
        sequencialAtividade: atividade?.sequencialAtividade,
        textoComunicado: ReplaceChar(values.textoImpedimento),
        textoTitulo: "INFORMAÇÃO",
        textoDadosContribuinte: dadosOs.sequencial + dadosOs.inscricaoEstadual + dadosOs.cnpjCpf + dadosOs.razaoSocial,
        cabecalho: atividade?.nomeAtividade,
        anexos: values.anexos,
        impedimentos: values.impedimentos,
        listaExecutoresOs: values.listaExecutoresOs,
        cpfRepresentante: values?.cpfRepresentante,
        nomeRepresentante: values?.nomeRepresentante,
        cargoRepresentante: values?.cargoRepresentante,
        nuPaginaEdital: values?.nuPaginaEdital,
        dataComunicado: values?.dataComunicado,
        codigoRastreio: values.codigoRastreio,
        livrosFiscais: values.livrosFiscais,
        txDescricao: ReplaceChar(values.txDescricao),
        fgMonitoramento: values.fgMonitoramento,
        sequencialTipoAcaoFiscalNovo: values.sequencialTipoAcaoFiscalNovo,
        listaProcedimentos: values?.procedimentos,
        fgAnexoBo: values.fgAnexoBo,
        fgAnexoJornal: values.fgAnexoJornal,
        txParametrizacao: values.txParametrizacao,
        txJustificativa: ReplaceChar(values.txJustificativa),
        vlIcmsDevido: values.vlIcmsDevido,
        vlIcmsRecolhido: values.vlIcmsRecolhido,
        vlIcmsParcelado: values.vlIcmsParcelado,
        vlIcmsEstornado: values.vlIcmsEstornado,
        cdProcesso: values.cdProcesso,
        enderecoRepresentante: values.enderecoRepresentante,
        txPreliminares: ReplaceChar(values.txPreliminares),
        txProcedimentos: ReplaceChar(values.txProcedimentos),
        txConstatacoes: ReplaceChar(values.txConstatacoes),
        txOcorrencias: ReplaceChar(values.txOcorrencias),
        txResultado: ReplaceChar(values.txResultado),
        cdPgeProcesso: values.cdPgeProcesso,
      };

      if (dadosOs.sequencialComunicado) {
        salvarRequest(apiRoutes.fiscalizacao.ordemservico.alterarAtividade, parametros);
      } else {
        salvarRequest(apiRoutes.fiscalizacao.ordemservico.incluirAtividade, parametros);
      }
    },
  });

  const { excluirAtividade } = useExcluirAtividade({ dadosOs, atividade });

  const setGuardClient = useSetGuard();

  const { assinarAtividade, assinarIsLoading } = useAssinarAtividade({
    dadosOs,
    atividade,
    formik,
    setGuardClient,
  });
  const { gerarComunicado, gerarComunicadoIsLoading, gerarComunicadoErrors, gerarComunicadoData } = useGerarComunicado({
    dadosOs,
    atividade,
    formik,
  });

  const { setSubmitting, setErrors, errors, isSubmitting, isValid } = formik;
  useEffect(() => {
    if (salvarIsLoading) return;

    if (!salvarData && !salvarErrors) return;

    if (salvarData) {
      toast.success("Atividade salva com sucesso!");
      window.location.reload();
    }
    setSubmitting(false);
  }, [salvarData, salvarErrors, salvarIsLoading, setSubmitting]);

  useEffect(() => {
    if (salvarErrors && isValid && !salvarIsLoading) setErrors({ ...errors, ...salvarErrors });
    if (errors && isSubmitting) {
      if (errors.livrosFiscais) {
        toast.error(errors.livrosFiscais);
      }
    }
  }, [setSubmitting, setErrors, errors, isSubmitting, isValid, salvarErrors, salvarIsLoading]);

  useEffect(() => {
    if (gerarComunicadoData) {
      toast.success("Atividade salva com sucesso!");
      window.location.reload();
    }
    setSubmitting(false);
  }, [gerarComunicadoData, setSubmitting]);

  const { setValues } = formik;

  useEffect(() => {
    const texto = dadosOs.situacaoComunicado === "A" ? dadosOs.textoComunicado : atividade?.textoDocumento;
    const siglaAtividade = atividade?.siglaAtividade ? atividade?.siglaAtividade : "";
    const cpfRepresentante = dadosOs.cpfRepresentante ? dadosOs?.cpfRepresentante : "";
    const nomeRepresentante = dadosOs.nomeRepresentante ? dadosOs?.nomeRepresentante : "";
    const cargoRepresentante = dadosOs.cargoRepresentante ? dadosOs?.cargoRepresentante : "";
    const enderecoRepresentante = dadosOs.enderecoRepresentante ? dadosOs?.enderecoRepresentante : "";
    const nuPaginaEdital = dadosOs.nuPaginaEdital ? dadosOs?.nuPaginaEdital : "";
    let dataComunicado = dadosOs.dataComunicado ? dadosOs?.dataComunicado : new Date().toISOString();
    const codigoRastreio = dadosOs.codigoRastreio ? dadosOs?.codigoRastreio : "";
    const txDescricao = dadosOs.txDescricao ? dadosOs?.txDescricao : "";
    const fgMonitoramento = dadosOs.fgMonitoramento === "S" ?? false;
    const sequencialTipoAcaoFiscalNovo = dadosOs.sequencialTipoAcaoFiscalNovo
      ? dadosOs?.sequencialTipoAcaoFiscalNovo
      : "";
    const procedimentos = dadosOs.procedimentos ? dadosOs?.procedimentos : [];
    const fgAnexoBo = dadosOs.fgAnexoBo === "S" ?? false;
    const fgAnexoJornal = dadosOs.fgAnexoJornal === "S" ?? false;
    const txParametrizacao = dadosOs.txParametrizacao ? dadosOs?.txParametrizacao : [];
    const txJustificativa = dadosOs.txJustificativa ? dadosOs?.txJustificativa : "";
    // const livrosFiscais = dadosOs.livrosFiscais ? dadosOs?.livrosFiscais : [];
    const vlIcmsDevido = dadosOs.vlIcmsDevido ? dadosOs?.vlIcmsDevido : "0,00";
    const vlIcmsRecolhido = dadosOs.vlIcmsRecolhido ? dadosOs?.vlIcmsRecolhido : "0,00";
    const vlIcmsParcelado = dadosOs.vlIcmsParcelado ? dadosOs?.vlIcmsParcelado : "0,00";
    const vlIcmsEstornado = dadosOs.vlIcmsEstornado ? dadosOs?.vlIcmsEstornado : "0,00";
    const cdProcesso = dadosOs?.cdProcesso ? dadosOs?.cdProcesso : "";
    const txPreliminares = dadosOs?.txPreliminares ? dadosOs?.txPreliminares : "";
    const txProcedimentos = dadosOs?.txProcedimentos ? dadosOs?.txProcedimentos : "";
    const txConstatacoes = dadosOs?.txConstatacoes ? dadosOs?.txConstatacoes : "";
    const txResultado = dadosOs?.txResultado ? dadosOs?.txResultado : "";
    const cdPgeProcesso = dadosOs?.cdPgeProcesso ? dadosOs?.cdPgeProcesso : "";

    const limpaTexto = (txt) =>
      (txt || "")
        .replace(/<\/?p>/gi, "") // remove <p> e </p>
        .replace(/\s+/g, " ") // troca múltiplos espaços por um só
        .trim();

    let txOcorrencias;

    if (dadosOs.txOcorrencias === null) {
      txOcorrencias = (dadosOs?.ocorrencias || [])
        .map(
          (oc) => `
      <li>
        <div><strong>Ocorrência:</strong> ${limpaTexto(oc.dsOcorrencia)}</div>
        <div><strong>Infringência:</strong> ${limpaTexto(
          (oc.txInfringencia || "") + (oc.txCompInfringencia || ""),
        )}</div>
        <div><strong>Penalidade:</strong> ${limpaTexto((oc.txPenalidade || "") + (oc.txCompPenalidade || ""))}</div>
      </li>`,
        )
        .join("<br />");

      txOcorrencias = `<ol>${txOcorrencias}</ol>`;
    } else {
      txOcorrencias = dadosOs?.txOcorrencias || [];
    }

    // Quando ultimas atividade forem I1, IF1, M0, AULATEINDI
    if (dadosOs?.dataCiencia !== null && dadosOs?.situacaoComunicado !== "A") {
      if (
        atividade?.siglaAtividade &&
        (atividade?.siglaAtividade === "I1" ||
          atividade?.siglaAtividade === "IF1" ||
          atividade?.siglaAtividade === "M0" ||
          atividade?.siglaAtividade === "M4" ||
          atividade?.siglaAtividade === "AULATEINDI")
      ) {
        if (dataComunicado === undefined || dataComunicado === null || dataComunicado === "") {
          dataComunicado = dadosOs.dataCiencia;
        }
      }
    }

    if (atividade?.siglaAtividade === "M4" && dadosOs?.situacaoComunicado === "A") {
      dataComunicado = dadosOs.dataComunicado;
    }

    if (atividade?.siglaAtividade && atividade?.siglaAtividade === "IF4") {
      dataComunicado = new Date().toISOString();
    }

    setValues((curr) => ({
      ...curr,
      textoImpedimento: texto,
      impedimentos: dadosOs.impedimentos,
      cpfRepresentante,
      nomeRepresentante,
      cargoRepresentante,
      nuPaginaEdital,
      dataComunicado,
      siglaAtividade,
      codigoRastreio,
      livrosFiscais: dadosOs.livrosFiscais,
      dataUltimaAtividadeExecutada: dadosOs.dataUltimaAtividadeExecutada,
      txDescricao,
      fgMonitoramento,
      sequencialTipoAcaoFiscalNovo,
      procedimentos,
      fgAnexoBo,
      fgAnexoJornal,
      txParametrizacao,
      txJustificativa,
      vlIcmsDevido,
      vlIcmsRecolhido,
      vlIcmsParcelado,
      vlIcmsEstornado,
      cdProcesso,
      enderecoRepresentante,
      txPreliminares,
      txProcedimentos,
      txConstatacoes,
      txOcorrencias,
      txResultado,
      cdPgeProcesso,
    }));
  }, [
    setValues,
    dadosOs.situacaoComunicado,
    dadosOs.textoComunicado,
    atividade?.textoDocumento,
    atividade?.siglaAtividade,
    dadosOs.impedimentos,
    dadosOs.cpfRepresentante,
    dadosOs.nomeRepresentante,
    dadosOs.cargoRepresentante,
    dadosOs.nuPaginaEdital,
    dadosOs.dataComunicado,
    dadosOs.codigoRastreio,
    dadosOs.livrosFiscais,
    dadosOs.dataCiencia,
    dadosOs.dataUltimaAtividadeExecutada,
    dadosOs.txDescricao,
    dadosOs.fgMonitoramento,
    dadosOs.sequencialTipoAcaoFiscalNovo,
    dadosOs.procedimentos,
    dadosOs.fgAnexoBo,
    dadosOs.fgAnexoJornal,
    dadosOs.txParametrizacao,
    dadosOs.txJustificativa,
    dadosOs.vlIcmsDevido,
    dadosOs.vlIcmsRecolhido,
    dadosOs.vlIcmsParcelado,
    dadosOs.vlIcmsEstornado,
    dadosOs.cdProcesso,
    dadosOs.enderecoRepresentante,
    dadosOs.txPreliminares,
    dadosOs.txProcedimentos,
    dadosOs.txConstatacoes,
    dadosOs.txOcorrencias,
    dadosOs.txResultado,
    dadosOs.ocorrencias,
    dadosOs.cdPgeProcesso,
  ]);

  if (assinarIsLoading) {
    return (
      <>
        <span>
          <Spinner animation="border" role="status" />
          Preparando documento(s) para assinatura...
        </span>
      </>
    );
  }

  if (gerarComunicadoIsLoading) {
    return (
      <span>
        <Spinner animation="border" role="status" />
        Gerando comunicado...
      </span>
    );
  }

  if (!atividade?.sequencialAtividade) {
    return null;
  }

  return (
    <>
      {setGuardClient.guardLink}
      <FormAlert
        formik={formik}
        data={salvarData}
        errors={salvarErrors || setGuardClient.assinarErrors || gerarComunicadoErrors}
        isLoading={salvarIsLoading || setGuardClient.assinarIsLoading || gerarComunicadoIsLoading}
        dismissible
      />
      <Form noValidate onSubmit={formik.handleSubmit}>
        <Row className="mt-2">
          <Col>
            <Row className="text-right">
              <Col>
                <Button variant="primary" className="mr-3" disabled={desabilitaAssinar} onClick={formik.handleSubmit}>
                  Salvar
                </Button>
                {atividade.siglaAtividade === "DEDIENPRAR" ? (
                  <Button
                    variant="primary"
                    className="mr-3"
                    disabled={!formik.isValid && dadosOs?.situacaoComunicado !== "A"}
                    onClick={() => {
                      gerarComunicado();
                    }}
                  >
                    Gerar Comunicado
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    className="mr-3"
                    disabled={!formik.isValid || desabilitaAssinar}
                    onClick={() => {
                      setDesabilitaAssinar(true);
                      assinarAtividade();
                    }}
                  >
                    Assinar
                  </Button>
                )}

                {dadosOs.sequencialComunicado > 0 && (
                  <Button variant="primary" className="mr-3" onClick={() => excluirAtividade()}>
                    Excluir
                  </Button>
                )}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <h4>{atividade.nomeAtividade}</h4>
              </Col>
            </Row>

            {atividade.siglaAtividade === "AI1" ? (
              <>
                <FormRelCircunstanciado formik={formik} atividade="AI1" />
                <br />
                <Row>
                  {dadosOs.fgPermiteAnexo === "S" || atividade.fgPermiteAnexo === "S" ? (
                    <Form.Group as={Col} sm={12} md={12}>
                      <Form.Label>
                        <strong>Anexos</strong>
                      </Form.Label>{" "}
                      <br />
                      <SelectFileInput
                        id="tab-anexos-arquivos"
                        field="anexos"
                        formik={formik}
                        accept=".jpeg, .pdf, .xls, .xlsx, .doc, .docx"
                        multiple
                        save
                        describe
                      />
                    </Form.Group>
                  ) : null}
                </Row>
                <ConsultaAnexos
                  sqAcaoFiscal={dadosOs.acaoFiscal}
                  sqOrdemServico={dadosOs.sequencial}
                  sqComunicado={dadosOs.sequencialComunicado}
                  sqAtividade={dadosOs.sequencialAtividadeComunicado}
                  permissao
                />
              </>
            ) : (
              <TelaComunicado formik={formik} dadosOs={dadosOs} atividade={atividade} />
            )}

            <br />
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default InputsProximaAtividadeBase;
